/**
 * batch-60 vaste-monteur (FO-440) - DE pure bezettingsstatus-resolver.
 *
 * Eén bron voor "wie loopt deze dienst en klopt dat?", gedeeld door de
 * backoffice-vervanging-monitor, de klant-DTO's (afwezigheid, weekplanning,
 * jaarplanning) en de escalatie-cron. Zonder deze module zou elke consument zijn
 * eigen "vaste monteur staat er niet op, dus vervangen"-regel schrijven en
 * gegarandeerd uit de pas lopen (precies de klacht uit de aanleiding: het
 * portaal toonde alleen wat handmatig beoordeeld was, niet de werkelijkheid).
 *
 * DB-vrij en tijdzone-vrij: alle datums zijn kalenderdag-sleutels
 * (`yyyy-MM-dd`, de UTC-middernacht-conventie van een dagregel), zodat de
 * functies in de API, de backoffice en de tests identiek rekenen.
 *
 * De vier statussen uit de spec (§B.3) plus twee voor een dienst ZONDER vaste
 * monteur (losse inzet, §6.1: "alleen VAST/onbemand; geen vervangen-label"):
 *
 *   VAST                                vaste monteur staat erop, niet afwezig
 *   VERVANGEN                           een ander staat erop (met naam)
 *   NOG_TE_PLANNEN                      onbemand terwijl de vaste monteur afwezig is
 *   VASTE_MONTEUR_AFWEZIG_MAAR_GEPLAND  conflict: vaste monteur staat erop, maar is afwezig
 *   GEPLAND                             geen vaste monteur; iemand staat erop
 *   ONBEMAND                            geen vaste monteur (of niet afwezig); niemand staat erop
 *
 * "Losse inzet" krijgt bewust GEPLAND en niet VAST: de klant mag nooit
 * "vaste monteur" lezen bij iemand die toevallig één keer kwam.
 */

// ─── Statussen ───────────────────────────────────────────────────────────────
//
// De string-unions komen uit `@streep/types` (één bron voor API, backoffice en
// klant-app); hier alleen de labels en de regels erop.

import {
  type AfwezigheidSoort,
  type BezettingsStatus,
} from '@streep/types'

export type { AfwezigheidSoort, BezettingsStatus }

/** NL-labels voor de backoffice (de klant-app vertaalt zelf via i18n). */
export const BEZETTINGS_STATUS_LABELS: Record<BezettingsStatus, string> = {
  VAST: 'Vaste monteur',
  VERVANGEN: 'Vervangen',
  NOG_TE_PLANNEN: 'Nog te plannen',
  VASTE_MONTEUR_AFWEZIG_MAAR_GEPLAND: 'Conflict: vaste monteur afwezig',
  GEPLAND: 'Gepland',
  ONBEMAND: 'Onbemand',
}

/**
 * Statussen die in de monitor en de escalatie als "afwijking" tellen. VAST en
 * GEPLAND zijn de norm; ONBEMAND zonder vaste monteur hoort bij het vak
 * "In te plannen" (FO-398) en is hier geen vervangingsvraag.
 */
export const BEZETTINGS_AFWIJKINGEN: ReadonlySet<BezettingsStatus> = new Set<BezettingsStatus>([
  'VERVANGEN',
  'NOG_TE_PLANNEN',
  'VASTE_MONTEUR_AFWEZIG_MAAR_GEPLAND',
])

// ─── Afwezigheidsbronnen ─────────────────────────────────────────────────────
//
// De bronnen die de conflict-detector al kent (spec §B.4): goedgekeurd verlof
// (incl. FO-418-blokkades), ziekte, cursus en uitdienst (FO-424). De soort is
// INTERN: hij verlaat de server nooit richting de klant (§6.5).

/** Eén afwezigheidsperiode van één medewerker, kalenderdagen inclusief. */
export interface AfwezigheidPeriode {
  employeeId: string
  /** `yyyy-MM-dd`, eerste afwezige dag. */
  van: string
  /** `yyyy-MM-dd`, laatste afwezige dag (inclusief). `null` = open einde. */
  tot: string | null
  soort: AfwezigheidSoort
  /**
   * Bron-record (verlofaanvraag, ziekmelding, cursus-sessie) zodat de klant-
   * afwezigheidspagina er zijn opaque reactie-sleutel op kan bouwen. `null` bij
   * uitdienst (geen reageerbare context).
   */
  bronId: string | null
  /**
   * Optioneel tijdvak (`HH:mm`) waarin de afwezigheid op ELKE dag van de
   * periode geldt - een cursusdag van 08:15-12:30. Zonder tijdvak is de
   * medewerker de hele dag afwezig (verlof, ziekte, uitdienst). Een dienst
   * die het tijdvak niet raakt (middagdienst 14:15-23:30 naast een ochtend-
   * cursus) is dan GEEN afwezigheid: intern is dat bewust dubbel gepland.
   */
  startTijd?: string | null
  eindTijd?: string | null
  /**
   * FO-418: een 0-uurs verlofboeking op roostervrije dagen (5-ploegen-
   * weekend, ATV-blok). Voor een dagregel-slot blijft het een blokkade
   * (plannen op zo'n dag is een conflict), maar het is GEEN afwezigheid
   * zonder dagregel: de klant verwachtte de monteur die dag toch al niet.
   */
  roostervrij?: boolean
}

export interface AfwezigheidBron {
  soort: AfwezigheidSoort
  bronId: string | null
  /** Tijdvak van de bron op deze dag; `null` = hele dag. */
  startTijd: string | null
  eindTijd: string | null
  /** 0-uurs roostervrije verlofdag (FO-418); zie `AfwezigheidPeriode.roostervrij`. */
  roostervrij: boolean
}

/** Een dienst-tijdvak (`HH:mm`, over-middernacht toegestaan: eind <= start). */
export interface Tijdvak {
  start: string
  eind: string
}

function hhmmNaarMinuten(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim())
  if (!m) return null
  const uur = Number(m[1])
  const minuut = Number(m[2])
  if (uur > 24 || minuut > 59) return null
  return uur * 60 + minuut
}

/**
 * Overlappen twee tijdvakken op dezelfde kalenderdag? Een eind op of vóór de
 * start leest als "over middernacht" (nachtdienst 22:00-06:00). Een niet-
 * parsebaar tijdvak telt als overlappend: liever een onterecht "afwezig" dan
 * een gemiste afwezigheid door een corrupt tijdveld.
 */
export function tijdvakkenOverlappen(a: Tijdvak, b: Tijdvak): boolean {
  const aStart = hhmmNaarMinuten(a.start)
  const aEindRuw = hhmmNaarMinuten(a.eind)
  const bStart = hhmmNaarMinuten(b.start)
  const bEindRuw = hhmmNaarMinuten(b.eind)
  if (aStart === null || aEindRuw === null || bStart === null || bEindRuw === null) return true
  const aEind = aEindRuw <= aStart ? aEindRuw + 24 * 60 : aEindRuw
  const bEind = bEindRuw <= bStart ? bEindRuw + 24 * 60 : bEindRuw
  return aStart < bEind && bStart < aEind
}

/** Raakt een bron de dienst? Zonder dienst of zonder bron-tijdvak: altijd. */
export function bronRaaktDienst(bron: AfwezigheidBron, dienst: Tijdvak | null | undefined): boolean {
  if (!dienst || !bron.startTijd || !bron.eindTijd) return true
  return tijdvakkenOverlappen({ start: bron.startTijd, eind: bron.eindTijd }, dienst)
}

const DAG_MS = 86_400_000

function ymdVanUtc(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function utcVanYmd(ymd: string): Date {
  return new Date(`${ymd}T00:00:00.000Z`)
}

/** `ymd + n` kalenderdagen (UTC-rekenkunde, dus DST-vrij). */
export function schuifDag(ymd: string, n: number): string {
  return ymdVanUtc(new Date(utcVanYmd(ymd).getTime() + n * DAG_MS))
}

/**
 * Snelle opzoektabel (medewerker x kalenderdag). Bewust een eigen klasse i.p.v.
 * een losse Map: de consument mag niet weten HOE de sleutel is opgebouwd.
 */
export class AfwezigheidIndex {
  private readonly dagen = new Map<string, AfwezigheidBron[]>()

  constructor(private readonly venster: { van: string; tot: string }) {}

  /** Alleen dagen binnen het venster worden opgenomen (open einde = tot venster-einde). */
  voeg(periode: AfwezigheidPeriode): void {
    const van = periode.van > this.venster.van ? periode.van : this.venster.van
    const totRuw = periode.tot ?? this.venster.tot
    const tot = totRuw < this.venster.tot ? totRuw : this.venster.tot
    if (van > tot) return
    const startTijd = periode.startTijd && periode.eindTijd ? periode.startTijd : null
    const eindTijd = periode.startTijd && periode.eindTijd ? periode.eindTijd : null
    for (let dag = van; dag <= tot; dag = schuifDag(dag, 1)) {
      const sleutel = `${periode.employeeId}|${dag}`
      const lijst = this.dagen.get(sleutel) ?? []
      lijst.push({
        soort: periode.soort,
        bronId: periode.bronId,
        startTijd,
        eindTijd,
        roostervrij: periode.roostervrij === true,
      })
      this.dagen.set(sleutel, lijst)
    }
  }

  /**
   * Afwezig op deze dag - en, als een `dienst` is meegegeven, afwezig TIJDENS
   * die dienst. Een tijdgebonden bron (ochtendcursus) telt niet voor een
   * dienst die het tijdvak niet raakt; een hele-dag-bron telt altijd.
   */
  isAfwezig(employeeId: string | null | undefined, ymd: string, dienst?: Tijdvak | null): boolean {
    return this.bronnen(employeeId, ymd, dienst).length > 0
  }

  /** Hele dag afwezig: minstens één bron zonder tijdvak (verlof, ziekte, hele-dag-cursus). */
  isHeleDagAfwezig(employeeId: string | null | undefined, ymd: string): boolean {
    return this.bronnen(employeeId, ymd).some((b) => !b.startTijd || !b.eindTijd)
  }

  /**
   * De bronnen achter een afwezige dag, in invoervolgorde (leeg = niet
   * afwezig). Met `dienst` alleen de bronnen die die dienst raken.
   */
  bronnen(
    employeeId: string | null | undefined,
    ymd: string,
    dienst?: Tijdvak | null,
  ): AfwezigheidBron[] {
    if (!employeeId) return []
    const alle = this.dagen.get(`${employeeId}|${ymd}`) ?? []
    if (!dienst) return alle
    return alle.filter((b) => bronRaaktDienst(b, dienst))
  }

  /** Aantal (medewerker x dag)-combinaties; handig voor tests en tellers. */
  get grootte(): number {
    return this.dagen.size
  }
}

/**
 * Bouwt de index over een venster. Het venster is verplicht: een uitdienst-
 * periode heeft een open einde en zou anders eindeloos expanderen.
 */
export function maakAfwezigheidIndex(
  periodes: ReadonlyArray<AfwezigheidPeriode>,
  venster: { van: string; tot: string },
): AfwezigheidIndex {
  const index = new AfwezigheidIndex(venster)
  for (const p of periodes) index.voeg(p)
  return index
}

// ─── De resolver ─────────────────────────────────────────────────────────────

export interface BezettingsInput {
  /** Kalenderdag van de dagregel (`yyyy-MM-dd`). */
  datum: string
  /** Wie op de dagregel staat; `null` = onbemand. */
  geplandeEmployeeId: string | null
  /** De vaste monteur van dit slot; `null` = losse inzet. */
  vasteMonteurId: string | null
  index: AfwezigheidIndex
  /**
   * Geplande tijden van de dagregel. Zonder dienst rekent de resolver per hele
   * dag (oude gedrag); mét dienst raakt een tijdgebonden afwezigheid
   * (ochtendcursus) een middagdienst niet.
   */
  dienst?: Tijdvak | null
}

export interface BezettingsResultaat {
  status: BezettingsStatus
  vasteMonteurId: string | null
  geplandeEmployeeId: string | null
  /** De vaste monteur is op deze dag afwezig (verlof/ziekte/cursus/uitdienst). */
  vasteMonteurAfwezig: boolean
  /** Wie er ook staat: is die persoon zelf afwezig op deze dag? */
  geplandeAfwezig: boolean
  /** Bij VERVANGEN: de id van de vervanger (= `geplandeEmployeeId`). */
  vervangerId: string | null
}

/**
 * Zie de module-toelichting voor de beslistabel. Puur; `index` levert de
 * afwezigheid.
 */
export function bepaalBezettingsStatus(input: BezettingsInput): BezettingsResultaat {
  const { datum, geplandeEmployeeId, vasteMonteurId, index, dienst } = input
  const vasteMonteurAfwezig = index.isAfwezig(vasteMonteurId, datum, dienst)
  const geplandeAfwezig = index.isAfwezig(geplandeEmployeeId, datum, dienst)
  const basis = {
    vasteMonteurId,
    geplandeEmployeeId,
    vasteMonteurAfwezig,
    geplandeAfwezig,
    vervangerId: null as string | null,
  }

  if (!vasteMonteurId) {
    return { ...basis, status: geplandeEmployeeId ? 'GEPLAND' : 'ONBEMAND' }
  }
  if (geplandeEmployeeId === vasteMonteurId) {
    return {
      ...basis,
      status: vasteMonteurAfwezig ? 'VASTE_MONTEUR_AFWEZIG_MAAR_GEPLAND' : 'VAST',
    }
  }
  if (geplandeEmployeeId) {
    return { ...basis, status: 'VERVANGEN', vervangerId: geplandeEmployeeId }
  }
  return { ...basis, status: vasteMonteurAfwezig ? 'NOG_TE_PLANNEN' : 'ONBEMAND' }
}

/**
 * Klant-lezing van een dagregel (§C.6): de klant ziet de vaste monteur, en bij
 * afwezigheid "vervangen door {naam}" of "vervanging wordt ingepland". NOOIT de
 * naam van iemand die er niet is - dus ook niet die van een geplande monteur die
 * zelf afwezig is (conflict, of losse inzet met een zieke monteur).
 */
export interface KlantBezettingsLezing {
  /** Toon de naam van de geplande monteur (false = "wordt ingepland"). */
  toonGeplandeNaam: boolean
  /** De geplande monteur is een vervanger van de vaste monteur. */
  isVervanging: boolean
  /** Er is een vaste monteur die afwezig is en er staat (nog) geen vervanger. */
  vervangingWordtIngepland: boolean
}

export function klantBezettingsLezing(r: BezettingsResultaat): KlantBezettingsLezing {
  switch (r.status) {
    case 'VAST':
      return { toonGeplandeNaam: true, isVervanging: false, vervangingWordtIngepland: false }
    case 'VERVANGEN':
      // Een vervanger die zelf afwezig is, is ook "iemand die er niet is".
      return r.geplandeAfwezig
        ? { toonGeplandeNaam: false, isVervanging: false, vervangingWordtIngepland: true }
        : { toonGeplandeNaam: true, isVervanging: true, vervangingWordtIngepland: false }
    case 'NOG_TE_PLANNEN':
    case 'VASTE_MONTEUR_AFWEZIG_MAAR_GEPLAND':
      return { toonGeplandeNaam: false, isVervanging: false, vervangingWordtIngepland: true }
    case 'GEPLAND':
      return {
        toonGeplandeNaam: !r.geplandeAfwezig,
        isVervanging: false,
        vervangingWordtIngepland: r.geplandeAfwezig,
      }
    case 'ONBEMAND':
      return { toonGeplandeNaam: false, isVervanging: false, vervangingWordtIngepland: false }
  }
}

// ─── Slot-koppeling (2-mans, §6.3) ───────────────────────────────────────────

export interface SlotDagregel {
  id: string
  employeeId: string | null
  /** `WerkbonDayEntry.position` - alleen als stabiele tiebreak. */
  position: number
}

export interface SlotVasteMonteur {
  positie: number
  employeeId: string
}

export interface SlotKoppeling<T extends SlotDagregel> {
  dagregel: T
  positie: number
  vasteMonteurId: string | null
}

/**
 * Koppelt de dagregels van één werkbon op één datum aan de vaste-monteur-slots.
 *
 * Naïef "op volgorde" gaat mis bij een 2-mans-bon waar de tweede vaste monteur
 * op de eerste regel staat: dan zouden beide slots "vervangen" lezen. Daarom:
 * eerst elke dagregel waarvan de monteur zélf een vaste monteur is aan zijn
 * eigen slot; de rest vult de overgebleven slots in positie-volgorde. Meer
 * dagregels dan slots -> de extra regels krijgen geen vaste monteur (losse
 * inzet); meer slots dan regels -> de lege slots leveren geen rij (er is geen
 * dagregel om een status aan te hangen).
 */
export function koppelSlots<T extends SlotDagregel>(
  dagregels: ReadonlyArray<T>,
  vasteMonteurs: ReadonlyArray<SlotVasteMonteur>,
): SlotKoppeling<T>[] {
  const slots = [...vasteMonteurs].sort((a, b) => a.positie - b.positie)
  const gesorteerd = [...dagregels].sort((a, b) => a.position - b.position || a.id.localeCompare(b.id))
  const slotVanMonteur = new Map(slots.map((s) => [s.employeeId, s]))
  const bezetteSlots = new Set<number>()
  const uit: SlotKoppeling<T>[] = []
  const rest: T[] = []

  for (const d of gesorteerd) {
    const eigen = d.employeeId ? slotVanMonteur.get(d.employeeId) : undefined
    if (eigen && !bezetteSlots.has(eigen.positie)) {
      bezetteSlots.add(eigen.positie)
      uit.push({ dagregel: d, positie: eigen.positie, vasteMonteurId: eigen.employeeId })
    } else {
      rest.push(d)
    }
  }

  const vrijeSlots = slots.filter((s) => !bezetteSlots.has(s.positie))
  // Losse regels krijgen een positie ná het hoogste bestaande slot (slots hoeven
  // niet aaneengesloten te zijn).
  let volgendeLosPositie = slots.reduce((max, s) => Math.max(max, s.positie), -1) + 1
  for (const d of rest) {
    const slot = vrijeSlots.shift()
    if (slot) {
      uit.push({ dagregel: d, positie: slot.positie, vasteMonteurId: slot.employeeId })
    } else {
      uit.push({ dagregel: d, positie: volgendeLosPositie++, vasteMonteurId: null })
    }
  }

  return uit.sort((a, b) => a.positie - b.positie)
}

// ─── Afleiding uit de planhistorie (§A.1) ────────────────────────────────────

export interface AfleidingDagregel {
  datum: string
  employeeId: string | null
}

export interface AfleidingOpties {
  /** Minimaal aandeel (0-100) van de bemande dagen dat één monteur liep. */
  drempelPercentage: number
  /** Minimaal aantal dagen waarop de afleiding gebaseerd moet zijn. */
  minimumDagen: number
}

export interface AfleidingVoorstel {
  positie: number
  employeeId: string
  /** Aandeel (0-100) van de bemande dagen in de historie. */
  percentage: number
  /** Aantal dagen waarop deze monteur de dienst liep. */
  dagen: number
}

/** Defaults; via een systeeminstelling overschrijfbaar (`vaste-monteur.ts`). */
export const VASTE_MONTEUR_AFLEIDING_DEFAULTS = {
  drempelPercentage: 60,
  minimumDagen: 4,
  vensterWeken: 8,
} as const

/**
 * Wie liep deze dienst overwegend? Telt per monteur de distinct kalenderdagen
 * in de historie en zet dat af tegen het aantal bemande dagen. Het aantal
 * slots is het maximum aantal bemande dagregels op één datum (2-mans = 2). Per
 * slot wint de monteur met het hoogste aandeel dat de drempel haalt; wie de
 * drempel of het minimum niet haalt levert geen voorstel (liever geen vaste
 * monteur dan een verkeerde).
 *
 * Bij een 2-mans-dienst is de drempel per monteur de helft: twee vaste monteurs
 * die elk elke dag liepen hebben elk 100% van "hun" slot, maar tellen elk voor
 * 100% van de bemande dagen - de drempel wordt daarom niet gedeeld door het
 * aantal slots (elke monteur wordt op zijn eigen dagen-aandeel getoetst).
 */
export function leidVasteMonteursAf(
  historie: ReadonlyArray<AfleidingDagregel>,
  opties: AfleidingOpties = VASTE_MONTEUR_AFLEIDING_DEFAULTS,
): AfleidingVoorstel[] {
  const dagenPerMonteur = new Map<string, Set<string>>()
  const perDatum = new Map<string, number>()
  for (const h of historie) {
    if (!h.employeeId) continue
    const set = dagenPerMonteur.get(h.employeeId) ?? new Set<string>()
    set.add(h.datum)
    dagenPerMonteur.set(h.employeeId, set)
    perDatum.set(h.datum, (perDatum.get(h.datum) ?? 0) + 1)
  }
  const bemandeDagen = perDatum.size
  if (bemandeDagen === 0) return []
  const slots = Math.max(1, ...perDatum.values())

  const kandidaten = [...dagenPerMonteur.entries()]
    .map(([employeeId, set]) => ({
      employeeId,
      dagen: set.size,
      percentage: Math.round((set.size / bemandeDagen) * 100),
    }))
    .filter((k) => k.percentage >= opties.drempelPercentage && k.dagen >= opties.minimumDagen)
    .sort(
      (a, b) =>
        b.percentage - a.percentage ||
        b.dagen - a.dagen ||
        a.employeeId.localeCompare(b.employeeId),
    )

  return kandidaten.slice(0, slots).map((k, i) => ({
    positie: i,
    employeeId: k.employeeId,
    percentage: k.percentage,
    dagen: k.dagen,
  }))
}

// ─── Afwezig zonder dagregel (regulier ingezette monteur) ────────────────────
//
// De slot-resolver hierboven hangt een afwezigheid altijd aan een DAGREGEL: de
// vaste monteur van dat slot, of wie erop staat. In de praktijk plant een
// planner een verlofdag simpelweg niet (of haalt de dag weg) en bevestigt
// niemand een vaste monteur - dan is er niets om aan te hangen en verdwijnt
// Rico's vrije vrijdag uit het portaal, terwijl de klant hem elke week ziet.
//
// Regel: een monteur die de klant REGULIER bedient (minstens `minimumDagen`
// aparte dagregel-dagen bij die klant binnen +/- `vensterDagen` rond de dag)
// en op een dag waarop de klant bediend wordt de HELE dag afwezig is zonder
// eigen dagregel, is voor de klant "afwezig". Wie de vervanger is weet deze
// regel niet (dat staat in de planning zelf); hij claimt dus nooit "wordt
// ingepland" - de bezetting van die dag is al zichtbaar op het bord.

export interface ReguliereInzetOpties {
  /** Halve breedte van het venster rond de dag, in kalenderdagen. */
  vensterDagen: number
  /** Minimum aantal aparte dagregel-dagen in dat venster (de dag zelf niet meegeteld). */
  minimumDagen: number
}

export const REGULIERE_INZET_DEFAULTS: ReguliereInzetOpties = {
  vensterDagen: 28,
  minimumDagen: 5,
}

/** Bedient deze monteur de klant regulier rond `datum`? */
export function isReguliereInzet(
  inzetDagen: ReadonlySet<string>,
  datum: string,
  opties: ReguliereInzetOpties = REGULIERE_INZET_DEFAULTS,
): boolean {
  const van = schuifDag(datum, -opties.vensterDagen)
  const tot = schuifDag(datum, opties.vensterDagen)
  let aantal = 0
  for (const dag of inzetDagen) {
    if (dag === datum || dag < van || dag > tot) continue
    aantal += 1
    if (aantal >= opties.minimumDagen) return true
  }
  return false
}

export interface AfwezigZonderDagregelInput {
  /** Per monteur de dagen (`yyyy-MM-dd`) met een klant-zichtbare dagregel bij deze klant, RUIM rond het venster. */
  inzet: ReadonlyMap<string, ReadonlySet<string>>
  /** Dagen waarop de klant bediend wordt (minstens één klant-zichtbare dagregel, van wie dan ook). */
  bediendeDagen: ReadonlySet<string>
  /** Het te beoordelen venster (inclusief). */
  venster: { van: string; tot: string }
  /** Afwezigheid van de monteurs in `inzet`, over minstens het venster. */
  index: AfwezigheidIndex
  opties?: ReguliereInzetOpties
}

export interface AfwezigZonderDagregel {
  employeeId: string
  datum: string
  /** De hele-dag-bronnen (nooit UITDIENST of een roostervrije 0-uurs-dag). */
  bronnen: AfwezigheidBron[]
}

export function bepaalAfwezigZonderDagregel(input: AfwezigZonderDagregelInput): AfwezigZonderDagregel[] {
  const opties = input.opties ?? REGULIERE_INZET_DEFAULTS
  const uit: AfwezigZonderDagregel[] = []
  for (const [employeeId, dagen] of input.inzet) {
    for (let dag = input.venster.van; dag <= input.venster.tot; dag = schuifDag(dag, 1)) {
      // Eigen dagregel die dag: de slot-resolver oordeelt (conflict/vervangen).
      if (dagen.has(dag)) continue
      // Niemand bij de klant die dag (weekend, sluiting): geen afwezigheid.
      if (!input.bediendeDagen.has(dag)) continue
      // Hele dag, geen uitdienst, geen roostervrije 0-uurs-boeking (FO-418):
      // op zijn vrije rotatiedag is een monteur niet "afwezig" voor de klant.
      const bronnen = input.index
        .bronnen(employeeId, dag)
        .filter((b) => (!b.startTijd || !b.eindTijd) && b.soort !== 'UITDIENST' && !b.roostervrij)
      if (bronnen.length === 0) continue
      if (!isReguliereInzet(dagen, dag, opties)) continue
      uit.push({ employeeId, datum: dag, bronnen })
    }
  }
  return uit.sort((a, b) => a.datum.localeCompare(b.datum) || a.employeeId.localeCompare(b.employeeId))
}

// ─── Tellers voor monitor en escalatie ───────────────────────────────────────

/** Ligt `datum` binnen [vandaag, vandaag + drempelDagen] (inclusief)? */
export function binnenDrempel(datum: string, vandaag: string, drempelDagen: number): boolean {
  return datum >= vandaag && datum <= schuifDag(vandaag, drempelDagen)
}
